// Apolo IDE — TurnFeedback (E6-WS3-T03: sinal manual de qualidade por turno).
//
// Dois botões ghost (👍/👎) no header de um turno do agente, no mesmo estilo do
// TurnCopyButton do ConversationFeed: ícone lucide, text-apolo-textTertiary,
// hover apolo-surface2. Clicar registra um "outcome" otimista (mutuamente
// exclusivo) e dispara um POST best-effort para /api/learning/outcomes.
//
// [SEC] E6-WS3-T03 — CRÍTICO: o payload carrega SOMENTE ids + outcome.
// NUNCA inclua conteúdo/texto do turno. O feedback é um sinal de aprendizado,
// não um vazamento do que foi conversado. Mantenha o body mínimo.

import { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { apiFetch } from '../../../lib/api.js';

const COUNT_KEY_PREFIX = 'apolo:turnFeedbackCounts:';

function readCounts(sessionId, turnId) {
  try {
    const raw = localStorage.getItem(`${COUNT_KEY_PREFIX}${sessionId}:${turnId}`);
    if (!raw) return { positive: 0, negative: 0 };
    const parsed = JSON.parse(raw);
    return {
      positive: Number(parsed.positive) || 0,
      negative: Number(parsed.negative) || 0,
    };
  } catch {
    return { positive: 0, negative: 0 };
  }
}

function bumpCount(sessionId, turnId, outcome) {
  const prev = readCounts(sessionId, turnId);
  const next = {
    ...prev,
    [outcome === 'positive' ? 'positive' : 'negative']: prev[outcome === 'positive' ? 'positive' : 'negative'] + 1,
  };
  try {
    localStorage.setItem(`${COUNT_KEY_PREFIX}${sessionId}:${turnId}`, JSON.stringify(next));
  } catch {
    /* private mode */
  }
  return next;
}

export default function TurnFeedback({ sessionId, turnId }) {
  // null | 'positive' | 'negative' — otimista, mutuamente exclusivo.
  const [selected, setSelected] = useState(null);
  const [counts, setCounts] = useState(() => readCounts(sessionId, turnId));
  const [pulse, setPulse] = useState(null);

  async function send(outcome) {
    setSelected(outcome);
    setPulse(outcome);
    setTimeout(() => setPulse(null), 350);
    setCounts(bumpCount(sessionId, turnId, outcome));
    try {
      // [SEC] body mínimo: apenas ids + outcome. Sem content/text do turno.
      await apiFetch('/api/learning/outcomes', {
        method: 'POST',
        body: {
          outcome,
          signal_source: 'manual',
          session_id: sessionId,
          metadata: { turn_id: turnId },
        },
      });
    } catch {
      // best-effort: feedback é sinal, não bloqueia a UI se a rede falhar.
    }
  }

  const base =
    'inline-flex h-6 w-6 items-center justify-center rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50';

  return (
    <span className="rx-fb inline-flex items-center gap-1" role="group" aria-label="Feedback do turno">
      <button
        type="button"
        onClick={() => send('positive')}
        aria-label="Bom"
        aria-pressed={selected === 'positive'}
        title={counts.positive ? `Bom (${counts.positive})` : 'Bom'}
        className={`${base} ${pulse === 'positive' ? 'rx-fb-pulse' : ''} ${
          selected === 'positive'
            ? 'text-apolo-success'
            : 'text-apolo-textTertiary hover:bg-apolo-surface2 hover:text-apolo-textPrimary'
        }`}
      >
        <ThumbsUp size={12} aria-hidden="true" />
      </button>
      {counts.positive > 0 && (
        <span className="rx-fb-count text-apolo-success" aria-hidden="true">{counts.positive}</span>
      )}
      <button
        type="button"
        onClick={() => send('negative')}
        aria-label="Ruim"
        aria-pressed={selected === 'negative'}
        title={counts.negative ? `Ruim (${counts.negative})` : 'Ruim'}
        className={`${base} ${pulse === 'negative' ? 'rx-fb-pulse' : ''} ${
          selected === 'negative'
            ? 'text-apolo-error'
            : 'text-apolo-textTertiary hover:bg-apolo-surface2 hover:text-apolo-textPrimary'
        }`}
      >
        <ThumbsDown size={12} aria-hidden="true" />
      </button>
      {counts.negative > 0 && (
        <span className="rx-fb-count text-apolo-error" aria-hidden="true">{counts.negative}</span>
      )}
    </span>
  );
}
